import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import MenuIcon from "@mui/icons-material/Menu";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LogoutIcon from "@mui/icons-material/Logout";
import type { ReactNode } from "react";

export interface HeaderProps {
  appName: ReactNode;
  userLabel?: string;
  themeMode: "light" | "dark";
  onToggleTheme: () => void;
  onToggleSidebar?: () => void;
  onLogout?: () => void;
}

/** Presentational top app bar. All behavior is delegated via callback props. */
export function Header({
  appName,
  userLabel,
  themeMode,
  onToggleTheme,
  onToggleSidebar,
  onLogout,
}: HeaderProps) {
  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        // MUI's permanent Drawer paper defaults to zIndex.drawer (1200), above a
        // fixed AppBar's default zIndex.appBar (1100) — without this, the sidebar
        // renders on top of the header and hides its left portion (menu icon,
        // app name) behind it.
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Toolbar sx={{ gap: 1 }}>
        {onToggleSidebar ? (
          <IconButton edge="start" aria-label="toggle sidebar" onClick={onToggleSidebar}>
            <MenuIcon />
          </IconButton>
        ) : null}
        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
          {appName}
        </Typography>
        <Tooltip title={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
          <IconButton aria-label="toggle theme" onClick={onToggleTheme}>
            {themeMode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
        {userLabel ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
            <Avatar sx={{ width: 32, height: 32 }}>{userLabel.charAt(0).toUpperCase()}</Avatar>
            <Typography variant="body2">{userLabel}</Typography>
          </Box>
        ) : null}
        {onLogout ? (
          <Tooltip title="Log out">
            <IconButton aria-label="log out" onClick={onLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Toolbar>
    </AppBar>
  );
}
